import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HandHeart, HeartHandshake, HeartCrack, ArrowRight, Calendar, MapPin, Clock, Utensils, Shirt, Home as HomeIcon, LifeBuoy, Users, Heart, Compass } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SectionHeader, fmtDate } from "@/components/bits";
import { ShareButton } from "@/components/ShareModal";

const ICONS = { Utensils, Shirt, Home: HomeIcon, LifeBuoy, Users, HandHeart: Heart, Compass, Heart };

export default function Home() {
  const { site } = useSite();
  const sc = site?.site_content || {};
  const stats = site?.impact_stats || [];
  const [data, setData] = useState({ announcements: [], programs: [], events: [], gallery: { photos: [] } });

  useEffect(() => {
    Promise.all([
      api.get("/public/announcements?limit=3"),
      api.get("/public/programs"),
      api.get("/public/events"),
      api.get("/public/gallery"),
    ]).then(([a, p, e, g]) => setData({ announcements: a.data.slice(0, 3), programs: p.data.slice(0, 6), events: e.data.slice(0, 3), gallery: g.data }))
      .catch(() => {});
  }, []);

  const heroImg = sc.hero_image || "https://images.unsplash.com/photo-1562709902-31c9a3b1ad5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center" data-testid="hero">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Ministry volunteers serving the community" width="1600" height="900" fetchpriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <p className="text-brand-cyan font-semibold tracking-widest uppercase text-sm mb-4">Love in Action</p>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
              {sc.hero_title || "It's All About Jesus."}
            </h1>
            <p className="font-serif italic text-2xl text-brand-cyan/90 mt-3">{sc.hero_subtitle || "Serving our community. Helping families. Sharing hope."}</p>
            <p className="text-white/90 text-lg mt-6 leading-relaxed max-w-xl">{sc.hero_mission}</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-9">
              <Link to="/request-help" className="inline-flex items-center justify-center gap-2 bg-white text-teal font-bold py-4 px-7 rounded-full shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all" data-testid="hero-need-help">
                <HandHeart className="w-5 h-5" /> I Need Help
              </Link>
              <Link to="/get-involved" className="inline-flex items-center justify-center gap-2 bg-brand-terracotta hover:bg-[#D0694E] text-white font-bold py-4 px-7 rounded-full shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all" data-testid="hero-want-help">
                <HeartHandshake className="w-5 h-5" /> I Want to Help
              </Link>
            </div>
            <Link to="/request-prayer" className="inline-flex items-center gap-2 text-white/90 hover:text-white mt-6 font-medium group" data-testid="hero-prayer">
              <HeartCrack className="w-4 h-4" /> Request Prayer <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <div className="mt-8">
              <ShareButton label="Share This Page" title="It's All About Jesus — Outreach and Help Center" text="Serving our community. Helping families. Sharing hope." url={typeof window !== "undefined" ? window.location.origin + "/" : ""} className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 text-white font-semibold py-2.5 px-5 rounded-full transition-all" data-testid="home-share-btn" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold tracking-widest uppercase text-brand-terracotta mb-3">Our Mission</p>
            <h2 className="font-head text-3xl lg:text-4xl font-bold text-slate-900">{sc.mission_heading || "Love in Action"}</h2>
            <p className="text-lg text-slate-600 mt-5 leading-relaxed">{sc.mission_text}</p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-7 text-teal font-semibold hover:gap-3 transition-all" data-testid="home-learn-more">
              Learn More About Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1628717341663-0007b0ee2597?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Packing food and grocery bags for families" className="rounded-2xl h-56 w-full object-cover shadow-lg" />
            <img src="https://images.pexels.com/photos/6646903/pexels-photo-6646903.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Handing a box of food to a neighbor in need" className="rounded-2xl h-56 w-full object-cover shadow-lg mt-8" />
          </div>
        </div>
      </section>

      {/* HOW WE HELP */}
      {data.programs.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader center eyebrow="How We Help" title="Serving Our Neighbors" subtitle="Real needs, met with compassion. Every program is a way we share the love of Jesus." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.programs.map((p) => (
                <Link to="/programs" key={p.id} className="group bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-xl transition-all" data-testid={`home-program-${p.id}`}>
                  {p.image && <div className="h-44 overflow-hidden"><img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                  <div className="p-6">
                    <h3 className="font-head text-xl font-semibold text-slate-900">{p.name}</h3>
                    <p className="text-slate-600 mt-2 line-clamp-2">{p.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ANNOUNCEMENTS */}
      {data.announcements.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <SectionHeader eyebrow="Stay Connected" title="Latest Announcements" />
              <Link to="/announcements" className="hidden sm:inline-flex items-center gap-2 text-teal font-semibold hover:gap-3 transition-all mb-14" data-testid="home-view-all-announcements">View All <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {data.announcements.map((a) => (
                <Link to={`/announcements/${a.id}`} key={a.id} className="group bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-xl transition-all" data-testid={`home-announcement-${a.id}`}>
                  {a.image && <div className="h-40 overflow-hidden"><img src={a.image} alt={a.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                  <div className="p-6">
                    <p className="text-xs text-slate-400 font-medium">{fmtDate(a.publish_date)}{a.source === "facebook" && " · Facebook"}</p>
                    <h3 className="font-head text-lg font-semibold text-slate-900 mt-2 line-clamp-2">{a.title}</h3>
                    <p className="text-slate-600 mt-2 line-clamp-3 text-sm">{a.body}</p>
                    <span className="inline-flex items-center gap-1 text-teal font-medium text-sm mt-4">Read More <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EVENTS */}
      {data.events.length > 0 && (
        <section className="py-16 md:py-24 bg-teal text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-sm font-semibold tracking-widest uppercase text-brand-cyan mb-3">Mark Your Calendar</p>
                <h2 className="font-head text-3xl lg:text-4xl font-bold">Upcoming Events</h2>
              </div>
              <Link to="/events" className="hidden sm:inline-flex items-center gap-2 text-white font-semibold hover:gap-3 transition-all">All Events <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {data.events.map((e) => (
                <Link to="/events" key={e.id} className="bg-white/10 backdrop-blur rounded-2xl p-6 hover:bg-white/15 transition-colors" data-testid={`home-event-${e.id}`}>
                  <h3 className="font-head text-xl font-semibold">{e.name}</h3>
                  <div className="mt-4 space-y-2 text-white/85 text-sm">
                    <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-brand-cyan" /> {fmtDate(e.date)}</p>
                    {e.start_time && <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-cyan" /> {e.start_time}{e.end_time ? ` – ${e.end_time}` : ""}</p>}
                    {e.location && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-cyan" /> {e.location}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MINISTRY IN ACTION */}
      {data.gallery?.photos?.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader center eyebrow="Ministry in Action" title="Faith, Lived Out Loud" subtitle="Moments of hope from our outreach in the community." />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.gallery.photos.slice(0, 8).map((ph) => (
                <div key={ph.id} className="aspect-square overflow-hidden rounded-xl group">
                  <img src={ph.thumb_url || ph.image || ph.url} alt={ph.alt_text || ph.title || "Ministry photo"} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/gallery" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white font-semibold py-3 px-7 rounded-full transition-all shadow-md" data-testid="home-view-gallery">See Ministry in Action <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* IMPACT */}
      {stats.length > 0 && (
        <section className="py-16 md:py-24 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader center eyebrow="Our Impact" title={sc.impact_heading || "Together, We're Making a Difference"} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((s, i) => (
                <div key={i} className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" data-testid={`impact-${i}`}>
                  <p className="font-head text-4xl lg:text-5xl font-extrabold text-brand-cyan">{s.value}</p>
                  <p className="text-white/80 mt-2 font-medium">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-brand-terracotta text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-head text-3xl lg:text-4xl font-bold">Whatever you need — we're here.</h2>
          <p className="text-white/90 text-lg mt-4">Reach out today. You are seen, you are loved, and you matter.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/request-help" className="bg-white text-brand-terracotta font-bold py-4 px-8 rounded-full shadow-lg hover:-translate-y-0.5 transition-all">Request Help</Link>
            <Link to="/get-involved" className="bg-teal hover:bg-teal-dark font-bold py-4 px-8 rounded-full shadow-lg hover:-translate-y-0.5 transition-all">Get Involved</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
